import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Database, GitBranch, FileText, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const DataStructureCard = ({ icon: Icon, data, color, sourcesLabel }) => {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-900/40 border-blue-800',
    purple: 'text-purple-400 bg-purple-900/40 border-purple-800',
    orange: 'text-orange-400 bg-orange-900/40 border-orange-800',
  };

  const badgeClasses = {
    blue: 'bg-blue-900/60 text-blue-400',
    purple: 'bg-purple-900/60 text-purple-400',
    orange: 'bg-orange-900/60 text-orange-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
      className={`p-6 rounded-2xl border-2 ${colorClasses[color].split(' ').pop()} bg-zinc-950 shadow-sm hover:shadow-md transition-all h-full flex flex-col`}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className={`p-3 rounded-xl ${colorClasses[color]} bg-opacity-50 flex-shrink-0`}>
          <Icon size={24} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-zinc-100">{data.title}</h3>
        </div>
      </div>
      
      <p className="text-sm text-zinc-400 leading-relaxed mb-6 font-medium flex-1">
        {data.description}
      </p>

      <div className="space-y-4 mt-auto">
        <div>
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">{sourcesLabel}</p>
          <div className="flex flex-wrap gap-2">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeClasses[color]}`}>
              {data.sources}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ProcessingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const lang = t('processing');

  return (
    <section className="relative py-24 sm:py-32 bg-zinc-950" ref={ref}>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 block">
            {lang.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100">
            {lang.headline}{' '}
            <span className="font-semibold gradient-text">{lang.headlineHighlight}</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base leading-relaxed">
            {lang.subheadline}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          <DataStructureCard
            icon={Database}
            data={lang.structured}
            color="blue"
            sourcesLabel={lang.sourcesLabel}
          />
          <DataStructureCard
            icon={GitBranch}
            data={lang.semiStructured}
            color="purple"
            sourcesLabel={lang.sourcesLabel}
          />
          <DataStructureCard
            icon={FileText}
            data={lang.unstructured}
            color="orange"
            sourcesLabel={lang.sourcesLabel}
          />
        </div>
      </div>
    </section>
  );
};

export default ProcessingSection;
